import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../Icons';

/**
 * ProposalCarousel Component
 * Displays multiple images/videos in a carousel with navigation arrows,
 * dots indicator, and lightbox/enlarge functionality
 *
 * @param {string[]} images - Array of image/video URLs to display
 * @param {string} heading - Alt text for images
 */
const ProposalCarousel = ({ images = [], heading = 'Section media' }) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  if (!Array.isArray(images) || images.length === 0) {
    return null;
  }

  const hasMultiple = images.length > 1;
  const currentImage = images[carouselIndex];
  const isVideo = currentImage && currentImage.match(/\.(mp4|webm|ogg|m4v)$/i);

  const canGoPrevious = carouselIndex > 0;
  const canGoNext = carouselIndex < images.length - 1;

  const goToPrevious = (event, isInLightbox = false) => {
    event?.stopPropagation();
    if (isInLightbox) {
      setCarouselIndex((current) => (current === 0 ? images.length - 1 : current - 1));
    } else if (canGoPrevious) {
      setCarouselIndex(carouselIndex - 1);
    }
  };

  const goToNext = (event, isInLightbox = false) => {
    event?.stopPropagation();
    if (isInLightbox) {
      setCarouselIndex((current) => (current === images.length - 1 ? 0 : current + 1));
    } else if (canGoNext) {
      setCarouselIndex(carouselIndex + 1);
    }
  };

  const handleLightboxToggle = (event) => {
    event?.stopPropagation();
    setIsLightboxOpen(!isLightboxOpen);
  };

  const getMediaProps = (isInLightbox) => ({
    src: currentImage,
    alt: heading,
    style: {
      width: isInLightbox ? 'auto' : '100%',
      height: isInLightbox ? 'auto' : 'auto',
      maxHeight: isInLightbox ? '90vh' : '530px',
      maxWidth: isInLightbox ? '90vw' : '440px',
      objectFit: 'contain',
      borderRadius: isInLightbox ? '0' : '8px',
      display: 'block',
      margin: '0 auto',
    },
  });

  const renderMedia = (isInLightbox = false) => {
    const mediaProps = getMediaProps(isInLightbox);
    const mediaElement = isVideo ? <video {...mediaProps} autoPlay loop muted playsInline /> : <img {...mediaProps} />;

    // Wrap media in clickable div for lightbox toggle (only when not in lightbox)
    if (!isInLightbox) {
      return (
        <div
          onClick={handleLightboxToggle}
          style={{
            cursor: 'pointer',
            display: 'block',
          }}
        >
          {mediaElement}
        </div>
      );
    }

    return mediaElement;
  };

  const renderCarousel = (isInLightbox) => {
    // In lightbox, arrows always cycle through. Outside, they disable at start/end
    const isPrevDisabled = !isInLightbox && !canGoPrevious;
    const isNextDisabled = !isInLightbox && !canGoNext;

    const getArrowColor = (isDisabled) => {
      if (isInLightbox) {
        return '#FFFFFF';
      }
      if (isDisabled) {
        return 'rgba(157, 157, 157, 0.4)';
      }
      return '#000000';
    };

    return (
      <div className={['carousel', isInLightbox && 'carousel--lightbox'].filter(Boolean).join(' ')}>
        <div className="carousel__frame" style={{ position: 'relative' }}>
          {renderMedia(isInLightbox)}
          {hasMultiple && (
            <button
              onClick={(e) => goToPrevious(e, isInLightbox)}
              disabled={isPrevDisabled}
              className={['carousel__arrow', 'carousel__arrow--prev'].filter(Boolean).join(' ')}
              style={{ zIndex: 2 }}
              aria-label="Previous"
            >
              <ChevronLeftIcon color={getArrowColor(isPrevDisabled)} />
            </button>
          )}
          {hasMultiple && (
            <button
              onClick={(e) => goToNext(e, isInLightbox)}
              disabled={isNextDisabled}
              className={['carousel__arrow', 'carousel__arrow--next'].filter(Boolean).join(' ')}
              style={{ zIndex: 2 }}
              aria-label="Next"
            >
              <ChevronRightIcon color={getArrowColor(isNextDisabled)} />
            </button>
          )}
        </div>
        {!isInLightbox && hasMultiple && (
          <nav className="carousel__dots dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={['dots__dot', carouselIndex === index && 'dots__dot--active'].filter(Boolean).join(' ')}
                onClick={() => setCarouselIndex(index)}
              >
                Go to image {index + 1}
              </button>
            ))}
          </nav>
        )}
      </div>
    );
  };

  return (
    <div className="proposal-carousel">
      {renderCarousel(false)}
      {isLightboxOpen && (
        <div
          className="lightbox"
          onClick={() => setIsLightboxOpen(false)}
          style={{
            position: 'fixed',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className="lightbox__bg" />
          {renderCarousel(true)}
          <button className="lightbox__close" onClick={() => setIsLightboxOpen(false)}>
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default ProposalCarousel;
